/* eslint-disable react-hooks/exhaustive-deps */
import { withProtectedRoute } from "../../utils/HOC/withProtectedRoute";
import { useDrops } from "../../store/useDrops";
import { useEffect, useRef, useState } from "react";
import { DropsTable } from "./DropsTable";
import {
  FormSelect,
  PrimaryButton,
  RedButton,
  SearchInput,
} from "../../components";
import { useNavigate } from "react-router";
import { useMarketPricing } from "../../store/useMarketPricing";
import { useAppLoading } from "../../store/useAppLoading";
import { useDropLocations } from "../../store/useDropLocations";

let searchTimeout: string | number | NodeJS.Timeout | null | undefined = null;

function DropsScreen() {
  /** Hooks */
  const navigate = useNavigate();
  const {
    downloadDropsInCSV,
    downloadDropsInCSVByProduct,
    dropStatusStats,
    getDropStatusStats,
    drops,
    dropsLoading,
    dropsInitialized,
    getDrops,
    searchText,
    setSearchText,
    clearSearchText,
    hasNextPage,
    highlightedStatus,
    setHighlightedStatus,
    dropsLocationFilter,
    setDropsLocationFilter,
  } = useDrops();
  const { getAllMarketPrices, marketPrices } = useMarketPricing();
  const { dropLocations, getDropLocations } = useDropLocations();
  const { isLoading } = useAppLoading();
  const didMountRef = useRef(false);
  /** State */
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);

  /** Effects */
  useEffect(() => {
    getDropLocations();
    getDrops(1, 20);
    getDropStatusStats();
    getAllMarketPrices();
    return () => {
      clearSearchText();
      setHighlightedStatus(null);
    };
  }, []);

  useEffect(() => {
    // Skip first run so we don't double-fetch on initial mount
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }

    if (searchText === "") {
      setPage(1);
      getDrops(1, 20);
      return;
    }
    if (searchTimeout) clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
      setPage(1);
      setHighlightedStatus(null);
      getDrops(1, 20, searchText);
    }, 500);
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchText]);

  useEffect(() => {
    getDrops(page, 20, searchText);
  }, [highlightedStatus]);

  /** Callbacks */
  const addDrop = () => navigate("/add-drop");
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };
  const handleLocationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setDropsLocationFilter(value);
    setPage(1);
    getDrops(1, 20, searchText || undefined);
    getDropStatusStats();
  };

  return (
    <div>
      <div className="flex w-full justify-between items-center mb-6">
        <h1 className="font-satoshi text-2xl font-bold text-dark">Drops</h1>
        <div className="flex">
          <PrimaryButton
            onClick={addDrop}
            label="Add new drop"
            size="sm"
            className="h-10 mr-4 !bg-droppie-oranje !border-droppie-oranje active:!bg-droppie-oranje-dark"
          />

          <PrimaryButton
            onClick={downloadDropsInCSV}
            label="Download CSV"
            size="sm"
            className="h-10 !bg-droppie-oranje !border-droppie-oranje active:!bg-droppie-oranje-dark"
          />
        </div>
      </div>
      {/* Top Stats */}
      <section className="flex flex-col sm:flex-row w-full p-2 sm:p-7 bg-white rounded-xl shadow-none drop-shadow-none sm:h-28 mb-1 sm:mb-10">
        <div className="flex flex-1 sm:flex-col justify-between items-center  h-full py-2 sm:py-0">
          <div className="flex items-center mb-1">
            <p className="font-satoshi text-2xl text-cc-green font-bold">
              {dropStatusStats?.approved}
            </p>
          </div>
          <p className="font-satoshi font-medium text-sm text-gray-text ml-2 sm:ml-0">
            Approved drops
          </p>
        </div>
      </section>
      <section className="w-full px-7 py-3 bg-white rounded-xl shadow-none mb-3 flex flex-col sm:flex-row gap-3 sm:gap-4">
        <div className="w-full sm:w-56 shrink-0">
          <FormSelect
            label=""
            value={dropsLocationFilter}
            onChange={handleLocationChange}
            options={[
              { value: "all", label: "All locations" },
              ...(dropLocations?.map((loc) => ({
                value: loc?._id as string,
                label: (loc?.cost_place ?? loc?.name ?? loc?._id) as string,
              })) ?? []),
            ]}
            inputClassName="h-10"
          />
        </div>
        <div className="flex-1 min-w-0">
          <SearchInput
            value={searchText}
            onChange={handleSearch}
            placeholder="Search by drop ID, username, email..."
          />
        </div>
      </section>
      <section className="w-full px-2 sm:px-7 py-2 sm:py-7 bg-white rounded-xl shadow-none drop-shadow-none">
        {!dropsInitialized || isLoading || dropsLoading ? (
          <div className="flex items-center justify-center h-[240px] w-full">
            <svg
              aria-hidden="true"
              className="w-8 h-8 text-gray-200 animate-spin fill-primary-base"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
          </div>
        ) : !drops?.length ? (
          <p className="text-center font-satoshi text-base text-dark">
            There is no drop created yet.
          </p>
        ) : (
          <DropsTable />
        )}
      </section>
      {hasNextPage ? (
        <div className="flex flex-row w-full items-center justify-end mt-3">
          <PrimaryButton
            size="sm"
            className="h-10 !bg-droppie-oranje !border-droppie-oranje active:!bg-droppie-oranje-dark"
            onClick={() => {
              setPage(page + 1);
              if (searchText) {
                return getDrops(page + 1, 20, searchText);
              }
              getDrops(page + 1, 20);
            }}
            label="Load more"
          />
        </div>
      ) : null}
      {modal ? (
        <>
          <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
            <div className="relative w-10/12 sm:w-1/4 my-0 sm:my-6 mx-auto max-w-3xl">
              {/*content*/}
              <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
                {/*header*/}
                <div className="flex items-start justify-center p-5 border-b border-solid border-blueGray-200 rounded-t">
                  <h3 className="text-xl font-semibold font-satoshi text-dark">
                    Select drops that contain product:
                  </h3>
                </div>
                {/*body*/}
                <div className="relative p-6 flex-auto">
                  <FormSelect
                    label="Product for filtering drops"
                    options={marketPrices?.map((price) => ({
                      value: price?._id as string,
                      label: price?.name as string,
                    }))}
                    value={selectedProduct as string}
                    onChange={(e) => setSelectedProduct(e.target.value)}
                    placeholder="Select product here"
                  />
                </div>
                {/*footer*/}
                <div className="flex items-center justify-end p-6 border-t border-solid border-blueGray-200 rounded-b">
                  <RedButton
                    label="Cancel"
                    className="mr-4"
                    onClick={() => setModal(false)}
                  />
                  <PrimaryButton
                    disabled={!selectedProduct}
                    label="Download CSV"
                    onClick={() => {
                      downloadDropsInCSVByProduct(selectedProduct as string);
                      setModal(false);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
        </>
      ) : null}
    </div>
  );
}

export default withProtectedRoute(DropsScreen);
